"use client";

import { useSyncExternalStore } from "react";

/**
 * Returns the current `Date.now()`, updated every `ms`. Returns `null` during
 * server render and initial hydration to keep SSR output stable.
 */
type Store = {
  subscribe: (cb: () => void) => () => void;
  getSnapshot: () => number;
};

const stores = new Map<number, Store>();

function getStore(ms: number): Store {
  const existing = stores.get(ms);
  if (existing) return existing;

  let value = Date.now();
  const listeners = new Set<() => void>();

  const id = setInterval(() => {
    value = Date.now();
    for (const l of listeners) l();
  }, ms);

  const store: Store = {
    subscribe(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
        // keep the interval alive (shared) for simplicity
      };
    },
    getSnapshot() {
      return value;
    },
  };

  // Ensure the interval doesn't keep Node processes alive in tooling contexts.
  // In browsers this is a no-op.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (id as any)?.unref?.();

  stores.set(ms, store);
  return store;
}

export function useTick(ms = 1000): number | null {
  const store = getStore(ms);
  return useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    () => null
  );
}
